import React, { useCallback, useEffect, useState } from 'react';

import { format, parseISO } from 'date-fns';

import { Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { formatPrice } from '../../utils/funcoes';

import { useToast } from '../../hooks/toast';

import { ContainerDados } from './styles';

import Loading from '../Loading';

import getResponseErrors from '../../utils/getResponseErrors';
import ControlesNotaFiscal, { ISituacaoNota } from '../ControlesNotaFiscal';
import { useAuth } from '../../hooks/auth';
import Paginacao from '../Paginacao';

interface IContaPR extends ISituacaoNota {
  CprCodigo: number;
  CprOrigem: number;
  CprCodOrigem: number;
  CprDataVenc: Date;
  CprDataPag: Date;
  CprDataExibe: string;
  CprHistorico: string;
  CprValor: number;
  CprValorPag: number;
  CprPago: boolean;
  CprPagoFormatado: string;
  CprValorExibe: string;
  dadosNota: ISituacaoNota;
}

interface ITotalPago {
  totalPagoAgenda: string;
  totalPagoCartao: string;
}

type PacienteProps = {
  pacCodigo: number;
};

interface IPaginacao {
  termoBusca: string;
  limite: number;
  pagina: number;
}

const PacientePagamento: React.FC<PacienteProps> = ({ pacCodigo }) => {
  const { addToast } = useToast();
  const { clinica } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pagamentosAgenda, setPagamentosAgenda] = useState<IContaPR[]>();
  const [
    paginacaoPagamentosAgenda,
    setPaginacaoPagamentosAgenda,
  ] = useState<IPaginacao>({ termoBusca: '', limite: 12, pagina: 1 });
  const [pagamentosCartao, setPagamentosCartao] = useState<IContaPR[]>();
  const [
    paginacaoPagamentosCartao,
    setPaginacaoPagamentosCartao,
  ] = useState<IPaginacao>({ termoBusca: '', limite: 12, pagina: 1 });
  const [totalPago, setTotalPago] = useState<ITotalPago>();

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IContaPR[]>(
          `/pacientes/pagamentos/${pacCodigo}/`,
        );

        const dadosFormatados: IContaPR[] = response.data.map(rel => {
          return {
            ...rel,
            CprValorExibe: rel.CprPago
              ? formatPrice(rel.CprValorPag)
              : formatPrice(rel.CprValor),
            CprDataExibe: rel.CprPago
              ? format(parseISO(rel.CprDataPag.toString()), 'dd/MM/yyyy')
              : format(parseISO(rel.CprDataVenc.toString()), 'dd/MM/yyyy'),
            CprPagoFormatado: rel.CprPago === true ? 'Sim' : 'Não',
            dadosNota: {
              NfeValorNota: rel.NfeValorNota,
              NfeSituacao: rel.NfeSituacao,
              NfeSerie: rel.NfeSerie,
              NfeNumeroNFSe: rel.NfeNumeroNFSe,
              NfeNumero: rel.NfeNumero,
              NfeMessage: rel.NfeMessage,
              NfeLinkNFSe: rel.NfeLinkNFSe,
              NfeDataEmissao: rel.NfeDataEmissao,
              NfeCprCodigo: rel.CprCodigo,
              NfeCodigoVerificacao: rel.NfeCodigoVerificacao,
              NfeArquivo: rel.NfeArquivo,
              linkNota:
                rel.NfeLinkNFSe && rel.NfeLinkNFSe.includes('google')
                  ? rel.NfeLinkNFSe
                  : rel.linkNota,
            },
          };
        });

        const pagAgenda = dadosFormatados.filter(pag => pag.CprOrigem === 1);
        const pagCartao = dadosFormatados.filter(pag => pag.CprOrigem === 2);

        const totalPagoAgenda = pagAgenda
          .filter(pag => pag.CprPago)
          .reduce((tot, item) => {
            return tot + Number(item.CprValorPag);
          }, 0);

        const totalPagoCartao = pagCartao
          .filter(pag => pag.CprPago)
          .reduce((tot, item) => {
            return tot + Number(item.CprValorPag);
          }, 0);

        setPagamentosAgenda(pagAgenda);
        setPagamentosCartao(pagCartao);
        setTotalPago({
          totalPagoAgenda: formatPrice(totalPagoAgenda),
          totalPagoCartao: formatPrice(totalPagoCartao),
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (pacCodigo && pacCodigo > 0) {
      loadDados();
    }
  }, [addToast, pacCodigo]);

  const handleProximaPaginaCartao = useCallback((page: number) => {
    setPaginacaoPagamentosCartao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  const handleProximaPaginaAgenda = useCallback((page: number) => {
    setPaginacaoPagamentosAgenda(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  const pagamentosCartaoPaginado = pagamentosCartao?.slice(
    (paginacaoPagamentosCartao.pagina - 1) * paginacaoPagamentosCartao.limite,
    paginacaoPagamentosCartao.pagina * paginacaoPagamentosCartao.limite,
  );

  const pagamentosAgendaPaginado = pagamentosAgenda?.slice(
    (paginacaoPagamentosAgenda.pagina - 1) * paginacaoPagamentosAgenda.limite,
    paginacaoPagamentosAgenda.pagina * paginacaoPagamentosAgenda.limite,
  );
  return (
    <>
      {loading && <Loading />}
      <ContainerDados className="rounded">
        <Row>
          <Col md={6}>
            <h5>Pagamentos Agenda</h5>
            {pagamentosAgendaPaginado && pagamentosAgendaPaginado.length > 0 ? (
              <>
                <h6>Total Pago: {totalPago?.totalPagoAgenda}</h6>
                <table
                  className="table table-hover table-sm table-font-small"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="bg-light text-dark">
                      <th style={{ width: '85px' }}>Data</th>
                      <th>Histórico</th>
                      <th style={{ width: '85px' }}>Valor</th>
                      <th style={{ width: '76px' }}>Nota Fiscal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagamentosAgendaPaginado.map(pag => (
                      <tr
                        key={`agenda-${pag.CprCodigo}`}
                        style={{
                          fontSize: '.7rem',
                          height: '30px',
                        }}
                      >
                        <td>{pag.CprDataExibe}</td>
                        <td>{pag.CprHistorico}</td>
                        <td>{pag.CprValorExibe}</td>
                        <td>
                          {pag.CprPago && (
                            <ControlesNotaFiscal
                              cprCodigo={pag.CprCodigo}
                              dadosNota={pag.dadosNota}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {paginacaoPagamentosAgenda && (
                  <Paginacao
                    limitePagina={paginacaoPagamentosAgenda?.limite || 12}
                    paginasVizinhas={2}
                    totalRegistros={pagamentosAgenda?.length || 0}
                    paginaInicio={paginacaoPagamentosAgenda?.pagina}
                    handleProximaPagina={handleProximaPaginaAgenda}
                  />
                )}
              </>
            ) : (
              <p>Sem pagamentos</p>
            )}
          </Col>
          <Col md={6}>
            <h5>Pagamentos Cartão Saúde</h5>
            {pagamentosCartaoPaginado && pagamentosCartaoPaginado.length > 0 ? (
              <>
                <h6>Total Pago: {totalPago?.totalPagoCartao}</h6>
                <table
                  className="table table-hover table-sm table-font-small"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="bg-light text-dark">
                      <th style={{ width: '85px' }}>Data</th>
                      <th>Histórico</th>
                      <th style={{ width: '85px' }}>Pago</th>
                      <th style={{ width: '85px' }}>Valor</th>
                      <th style={{ width: '76px' }}>Nota Fiscal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagamentosCartaoPaginado.map(pag => (
                      <tr
                        key={`agenda-${pag.CprCodigo}`}
                        style={{
                          fontSize: '.7rem',
                          height: '30px',
                        }}
                      >
                        <td>{pag.CprDataExibe}</td>
                        <td>{pag.CprHistorico}</td>
                        <td>{pag.CprPagoFormatado}</td>
                        <td>{pag.CprValorExibe}</td>
                        <td>
                          {pag.CprPago && clinica.CliEmiteNFCartao && (
                            <ControlesNotaFiscal
                              cprCodigo={pag.CprCodigo}
                              dadosNota={pag.dadosNota}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {paginacaoPagamentosCartao && (
                  <Paginacao
                    limitePagina={paginacaoPagamentosCartao?.limite || 12}
                    paginasVizinhas={2}
                    totalRegistros={pagamentosCartao?.length || 0}
                    paginaInicio={paginacaoPagamentosCartao?.pagina}
                    handleProximaPagina={handleProximaPaginaCartao}
                  />
                )}
              </>
            ) : (
              <p>Sem pagamentos</p>
            )}
          </Col>
        </Row>
      </ContainerDados>
    </>
  );
};

export default PacientePagamento;
